// Dependencies: cart-drawer-essentials.js

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setZipCodeCheckerLinkAccessibility();

    // Check for post-checkout cleanup on initialization
    this.handlePostCheckoutCleanup();

    // Check if cart should be opened after page refresh (e.g., after adding package or gift card)
    this.checkOpenCartAfterRefresh();

    // Listen for cart updates from custom events (like cart-drawer-essentials)
    document.addEventListener('cart:updated', (event) => {
      // Only re-initialize zip validator if it's needed (cart content changed significantly)
      if (event.detail?.contentChanged !== false) {
        this.initializeZipValidator();
      }
    });

    // Listen for cart updates from pubsub system (like product-form)
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Only re-initialize zip validator for significant cart changes (like item additions/removals)
      if (event.source === 'cart-items' || event.source === 'product-form' || event.source === 'cart-drawer') {
        this.initializeZipValidator();
      }
    });

    // Listen for weekly menu changes
    document.addEventListener('weeklyMenu:changed', () => {
      this.updateWeeklyMenuHeader();
    });

    // Add clear cart button listener
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-clear-button]')) {
        this.handleClearCart();
      }
    });

    // Add checkout button listener to track checkout attempts
    this.addEventListener('click', (event) => {
      const checkoutButton = event.target.closest('#CartDrawer-Checkout');
      if (checkoutButton && !checkoutButton.disabled) {
        this.handleCheckoutAttempt();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  setZipCodeCheckerLinkAccessibility() {
    // Use event delegation on document to handle clicks on zip code checker links
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href="#zip-code-checker"]');
      if (link) {
        event.preventDefault();
        this.open(link);
      }
    });
  }

  open(triggeredBy) {
    // Clear the flag immediately to prevent reopening on subsequent page loads
    localStorage.removeItem('openCartAfterRefresh');

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Update weekly menu header with current selection
    this.updateWeeklyMenuHeader();

    // Always initialize zip validator when cart drawer opens
    this.initializeZipValidator();

    // here the animation doesn't seem to always get triggered
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        
        // Ensure zip validator is initialized after transitions complete
        setTimeout(() => {
          if (typeof CartZipValidator !== 'undefined' && document.querySelector('.cart-zip-validator')) {
            window.cartZipValidator = new CartZipValidator();
          }
        }, 50);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Update essentials with new cart data
    const essentialsElement = this.querySelector('cart-drawer-essentials');
    if (essentialsElement) {
      essentialsElement.fetchRecommendedProducts();
    }

    // Update weekly menu header with current selection
    this.updateWeeklyMenuHeader();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      
      // Initialize zip validator AFTER the cart drawer is opened and content is rendered
      setTimeout(() => {
        if (typeof CartZipValidator !== 'undefined' && document.querySelector('.cart-zip-validator')) {
          window.cartZipValidator = new CartZipValidator();
        }
      }, 100);
    });
  }

  updateWeeklyMenuHeader() {
    const headerElement = this.querySelector('.cart-weekly-menu-header');
    const backToMenuElement = this.querySelector('.cart-back-to-menu');
    const backToMenuLink = this.querySelector('.cart-back-to-menu__link');
    
    if (!headerElement) return;

    // Check if WeeklyMenuUtils is available
    if (!window.WeeklyMenuUtils) {
      return;
    }

    const sessionData = window.WeeklyMenuUtils.getSessionData();

    if (sessionData.deliveryDate && sessionData.weeklyMenuTitle) {
      try {
        const deliveryDate = WeeklyMenuUtils.parseESTDate(sessionData.deliveryDate + 'T12:00:00');
        const formattedDate = deliveryDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric'
        });

        headerElement.textContent = `Ordering for: ${formattedDate.toUpperCase()}`;
      } catch (error) {
        console.error('Error formatting delivery date:', error);
        // Fallback to weekly menu title if date formatting fails
        if (sessionData.weeklyMenuTitle) {
          headerElement.textContent = `Ordering for: ${sessionData.weeklyMenuTitle.toUpperCase()}`;
        }
      }
      
      // Show and update the back to menu link if collection handle is available
      if (sessionData.weeklyMenuCollectionHandle && backToMenuElement && backToMenuLink) {
        backToMenuLink.href = `/collections/${sessionData.weeklyMenuCollectionHandle}`;
        backToMenuElement.style.display = 'block';
      } else if (backToMenuElement) {
        backToMenuElement.style.display = 'none';
      }
    } else {
      // No weekly menu data, use default text
      headerElement.textContent = '';
      
      // Hide the back to menu link
      if (backToMenuElement) {
        backToMenuElement.style.display = 'none';
      }
    }
  }

  initializeZipValidator() {
    // Initialize zip validator when cart content changes, but only if needed
    if (typeof CartZipValidator !== 'undefined' && document.querySelector('.cart-zip-validator')) {
      if (window.cartZipValidator) {
        // Only reinitialize if the validator is not properly bound to current DOM
        if (typeof window.cartZipValidator.isProperlyBound === 'function' && 
            !window.cartZipValidator.isProperlyBound()) {
          window.cartZipValidator.reinitialize();
        }
      } else {
        // Create new instance if none exists
        window.cartZipValidator = new CartZipValidator();
      }
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  checkOpenCartAfterRefresh() {
    // Check if we should open the cart after a page refresh
    const shouldOpenCart = localStorage.getItem('openCartAfterRefresh');

    if (shouldOpenCart === 'true') {
      // Open the cart after a short delay to ensure the page is fully loaded
      setTimeout(() => {
        this.open();
      }, 150);
    }
  }

  handlePostCheckoutCleanup() {
    const checkoutAttempted = localStorage.getItem('checkout_attempted');
    
    if (checkoutAttempted) {
      // Get current cart state from Shopify's global cart object or fetch it
      this.checkCartState().then(cartEmpty => {
        if (cartEmpty) {
          // Cart is empty and checkout was attempted - assume successful checkout
          this.clearPostCheckoutData();
        } else {
          // Cart has items but checkout was attempted - user likely abandoned checkout
          localStorage.removeItem('checkout_attempted');
          localStorage.removeItem('checkout_timestamp');
        }
      }).catch(error => {
        console.error('Error checking cart state:', error);
        // If we can't check cart state, clear old flags based on timestamp
        const checkoutTimestamp = localStorage.getItem('checkout_timestamp');
        const timestampAge = checkoutTimestamp ? Date.now() - parseInt(checkoutTimestamp) : 0;
        if (timestampAge > 60 * 60 * 1000) { // 1 hour
          localStorage.removeItem('checkout_attempted');
          localStorage.removeItem('checkout_timestamp');
        }
      });
    }
  }

  async checkCartState() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart.item_count === 0;
    } catch (error) {
      // Fallback: check if global cart object exists
      if (window.cart) {
        return window.cart.item_count === 0;
      }
      throw error;
    }
  }

  // handleCheckoutAttempt() {
  //   // Set flag when user clicks checkout button
  //   localStorage.setItem('checkout_attempted', 'true');
  //   localStorage.setItem('checkout_timestamp', Date.now().toString());
  // }

handleCheckoutAttempt() {
  const sessionData = window.WeeklyMenuUtils?.getSessionData();
  const cutoffDateString = window.WeeklyMenuUtils?.getSessionData()?.cutoffDate;
  console.log(cutoffDateString, 'cutoffDateString');
  if (!cutoffDateString) return;

  const cutoffDate = toEST(cutoffDateString);
  const now = new Date();

  console.log('Cutoff (EST):', formatInEST(cutoffDate));
  console.log('Now (EST):', formatInEST(now));

  const checkoutButton = document.querySelector('#CartDrawer-Checkout');
  const cutoffMessage = document.querySelector('#CartDrawer-CutoffMessage');
  if (!checkoutButton || !cutoffMessage) return;

  // If cutoff passed
  if (now.getTime() > cutoffDate.getTime()) {
    checkoutButton.disabled = true;
    cutoffMessage.style.display = 'block';
    let formattedDate = 'this date';

    // 🔑 Use deliveryDate (same source as "Ordering for")
    if (sessionData?.deliveryDate) {
      const deliveryDate = WeeklyMenuUtils.parseESTDate(
        sessionData.deliveryDate + 'T12:00:00'
      );

      formattedDate = deliveryDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      });
    }

    cutoffMessage.textContent = `We sold out for ${formattedDate}. Please clear your cart and select a new date.`;
    return;
  }

  // If still before cutoff
  checkoutButton.disabled = false;
  cutoffMessage.style.display = 'none';
  localStorage.setItem('checkout_attempted', 'true');
  localStorage.setItem('checkout_timestamp', Date.now().toString());
}


  clearPostCheckoutData() {
    // Clear checkout tracking
    localStorage.removeItem('checkout_attempted');
    localStorage.removeItem('checkout_timestamp');

    // Clear weekly menu data
    if (window.WeeklyMenuUtils && typeof window.WeeklyMenuUtils.clearSessionData === 'function') {
      window.WeeklyMenuUtils.clearSessionData();
    } else {
      localStorage.removeItem('weeklyMenuSelection');
    }

    localStorage.removeItem('cartValidZipCode');

    // Update UI elements
    this.updateWeeklyMenuHeader();

    // Reinitialize zip validator to reset state
    if (window.cartZipValidator && typeof window.cartZipValidator.reset === 'function') {
      window.cartZipValidator.reset();
    }
  }

  async handleClearCart() {
    const clearButton = this.querySelector('[data-cart-clear-button]');
    const buttonText = clearButton?.querySelector('.cart-drawer__clear-cart-text');

    if (clearButton && buttonText) {
      // Update button to loading state
      clearButton.disabled = true;
      buttonText.textContent = 'Clearing cart...';
    }

    try {
      // Use WeeklyMenuUtils if available, otherwise use direct cart clear API
      if (window.WeeklyMenuUtils && typeof window.WeeklyMenuUtils.clearCart === 'function') {
        await window.WeeklyMenuUtils.clearCart();
      } else {
        await this.clearCartDirect();
      }
    } catch (error) {
      console.error('Error clearing cart:', error);

      // Show user-friendly error message
      alert('There was an issue clearing your cart. Please try again.');
    } finally {
      // Reset button state on error
      if (clearButton && buttonText) {
        clearButton.disabled = false;
        buttonText.textContent = 'Clear Cart';
      }
    }
  }

  async clearCartDirect() {
    return new Promise((resolve, reject) => {
      fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => {
        if (response.ok) {
          location.reload();
          resolve();
        } else {
          reject(new Error('Failed to clear cart'));
        }
      })
      .catch(reject);
    });
  }
}

// --- Utility functions ---
function toEST(dateString) {
  const utcDate = new Date(dateString);

  // Check if New York is in daylight saving time for this date
  const isDST = isDSTinNewYork(utcDate);

  // Add 4 hours if DST (EDT), 5 hours if standard time (EST)
  const offset = isDST ? 4 : 5;
  utcDate.setHours(utcDate.getHours() + offset);

  console.log(`DST: ${isDST ? 'Yes (EDT, UTC-4)' : 'No (EST, UTC-5)'}`, utcDate);
  return utcDate;
}

function isDSTinNewYork(date) {
  // Create a date string in New York time zone
  const nyDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  // If the timezone offset in minutes is greater (less negative) than -300 (5 hours), it’s DST (UTC-4)
  return nyDate.getTimezoneOffset() < 300;
}


// For displaying in EST:
function formatInEST(date) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(date);
}


customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '.drawer__contents.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartDrawer-Footer',
        section: 'cart-drawer',
        selector: '.cart-drawer__footer',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
